import React, { useState, useEffect } from 'react';
import {
  Box, Grid, Card, CardContent, Typography, Chip, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, CircularProgress,
  Avatar, LinearProgress, alpha, IconButton, Tooltip, Skeleton, Button
} from '@mui/material';
import {
  Business, People, TrendingUp, Warning, Receipt,
  ArrowUpward, ArrowDownward, AccountBalance,
  CheckCircle, MonetizationOn, Assessment, Percent,
  MoreHoriz, OpenInNew, TrendingFlat,
  NotificationsActive, PersonAdd, Payment
} from '@mui/icons-material';
import { reportsAPI, credentialsAPI, remindersAPI } from '../services/api';
import { fullName } from '../utils/formatters';
import { useNavigate } from 'react-router-dom';
import { Add } from '@mui/icons-material';

const fmt = (v) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(v || 0);
const fmtFull = (v) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(v || 0);

const StatCard = ({ title, value, icon, gradient, subtitle, trend, onClick }) => (
  <Card sx={{
    position: 'relative', overflow: 'hidden', height: '100%',
    cursor: onClick ? 'pointer' : 'default',
    transition: 'all 0.3s cubic-bezier(0.4,0,0.2,1)',
    '&:hover': { transform: 'translateY(-4px)', boxShadow: '0 12px 24px rgba(0,0,0,0.08)' }
  }} onClick={onClick}>
    <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 }, position: 'relative', zIndex: 1 }}>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
        <Box sx={{ flex: 1 }}>
          <Typography sx={{
            fontSize: '0.7rem', fontWeight: 700, letterSpacing: '0.08em',
            textTransform: 'uppercase', color: 'text.secondary', mb: 1
          }}>
            {title}
          </Typography>
          <Typography sx={{ fontSize: '1.5rem', fontWeight: 800, lineHeight: 1.1, color: 'text.primary', mb: 0.5 }}>
            {value}
          </Typography>
          {subtitle && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
              {trend === 'up' && <ArrowUpward sx={{ fontSize: 14, color: 'success.main' }} />}
              {trend === 'down' && <ArrowDownward sx={{ fontSize: 14, color: 'error.main' }} />}
              {!trend && <TrendingFlat sx={{ fontSize: 14, color: 'text.secondary' }} />}
              <Typography sx={{
                fontSize: '0.72rem', fontWeight: 600,
                color: trend === 'up' ? 'success.main' : trend === 'down' ? 'error.main' : 'text.secondary'
              }}>
                {subtitle}
              </Typography>
            </Box>
          )}
        </Box>
        <Avatar sx={{
          width: 48, height: 48, borderRadius: 3,
          background: gradient,
          boxShadow: `0 4px 14px ${alpha((gradient?.split(',')[1]?.replace(')', '') || '#3b82f6').trim(), 0.3)}`
        }}>
          {icon}
        </Avatar>
      </Box>
    </CardContent>
    <Box sx={{
      position: 'absolute', top: -20, right: -20, width: 100, height: 100,
      borderRadius: '50%', background: gradient, opacity: 0.04
    }} />
  </Card>
);

const ProgressRow = ({ label, value, total, color }) => {
  const pct = total > 0 ? Math.min(Math.round((value / total) * 100), 100) : 0;
  return (
    <Box sx={{ mb: 2.5 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.8 }}>
        <Typography sx={{ fontSize: '0.82rem', fontWeight: 500, color: 'text.primary' }}>{label}</Typography>
        <Typography sx={{ fontSize: '0.82rem', fontWeight: 700, color }}>{fmtFull(value)}</Typography>
      </Box>
      <LinearProgress variant="determinate" value={pct} sx={{
        height: 6, borderRadius: 3, bgcolor: alpha(color, 0.1),
        '& .MuiLinearProgress-bar': { bgcolor: color, borderRadius: 3 }
      }} />
    </Box>
  );
};

const Dashboard = () => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [creds, setCreds] = useState([]);
  const [reminders, setReminders] = useState({ overdue: [], today: [], upcoming: [], counts: {} });

  useEffect(() => { document.title = 'Dashboard | MyPayPlus'; }, []);
  useEffect(() => {
    const load = async () => {
      try {
        const [d, c, r] = await Promise.all([
          reportsAPI.dashboard().catch(() => ({ data: null })),
          credentialsAPI.getExpiringAlerts().catch(() => ({ data: [] })),
          remindersAPI.getDashboard().catch(() => ({ data: { overdue: [], today: [], upcoming: [], counts: {} } }))
        ]);
        setData(d.data);
        setCreds(Array.isArray(c.data) ? c.data : []);
        setReminders(r.data || { overdue: [], today: [], upcoming: [], counts: {} });
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    };
    load();
  }, []);

  if (loading) {
    return (
      <Box>
        <Skeleton variant="text" width={200} height={40} sx={{ mb: 1 }} />
        <Skeleton variant="text" width={300} height={24} sx={{ mb: 3 }} />
        <Grid container spacing={2}>
          {[...Array(6)].map((_, i) => (
            <Grid item xs={6} sm={4} md={2} key={i}>
              <Skeleton variant="rounded" height={120} sx={{ borderRadius: 4 }} />
            </Grid>
          ))}
        </Grid>
      </Box>
    );
  }

  const p = data?.providers || {};
  const c = data?.contractors || {};
  const col = data?.collections || {};
  const inv = data?.invoices || {};

  return (
    <Box>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" sx={{ mb: 0.5 }}>Dashboard</Typography>
        <Typography variant="body2" color="text.secondary">
          Welcome back — here's your business overview for {new Date().toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
        </Typography>
      </Box>

      {/* Quick Actions */}
      <Box sx={{ display: 'flex', gap: 1.5, mb: 3, flexWrap: 'wrap' }}>
        <Button variant="contained" startIcon={<Add />} onClick={() => navigate('/collections')}
          sx={{ background: 'linear-gradient(135deg, #f59e0b, #d97706)', boxShadow: '0 4px 14px rgba(245,158,11,0.3)', fontWeight: 700 }}>
          Add Collection
        </Button>
        <Button variant="contained" startIcon={<Business />} onClick={() => navigate('/providers')}
          sx={{ background: 'linear-gradient(135deg, #3b82f6, #2563eb)', fontWeight: 700 }}>
          New Provider
        </Button>
        <Button variant="contained" startIcon={<PersonAdd />} onClick={() => navigate('/contractors')}
          sx={{ background: 'linear-gradient(135deg, #8b5cf6, #7c3aed)', fontWeight: 700 }}>
          New Contractor
        </Button>
        <Button variant="contained" startIcon={<Receipt />} onClick={() => navigate('/invoices')}
          sx={{ background: 'linear-gradient(135deg, #10b981, #059669)', fontWeight: 700 }}>
          Generate Invoices
        </Button>
      </Box>

      {/* Row 1: 6 KPI Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={6} sm={4} md={2}>
          <StatCard title="Providers" value={p.active || 0} subtitle={`${p.total || 0} total`}
            gradient="linear-gradient(135deg, #3b82f6, #2563eb)" icon={<Business sx={{ fontSize: 22 }} />} trend="up"
            onClick={() => navigate('/providers')} />
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <StatCard title="Contractors" value={c.active || 0} subtitle={`${c.total || 0} total`}
            gradient="linear-gradient(135deg, #8b5cf6, #7c3aed)" icon={<People sx={{ fontSize: 22 }} />} trend="up"
            onClick={() => navigate('/contractors')} />
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <StatCard title="Collections" value={fmt(col.monthlyCollected)} subtitle={`${col.monthlyCount || 0} this month`}
            gradient="linear-gradient(135deg, #f59e0b, #d97706)" icon={<MonetizationOn sx={{ fontSize: 22 }} />}
            onClick={() => navigate('/collections')} />
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <StatCard title="Billed" value={fmt(col.monthlyBilled)} subtitle={`${col.total || 0} total entries`}
            gradient="linear-gradient(135deg, #06b6d4, #0891b2)" icon={<AccountBalance sx={{ fontSize: 22 }} />}
            onClick={() => navigate('/collections')} />
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <StatCard title="Profit" value={fmt(col.monthlyProfit)} subtitle="This month"
            gradient="linear-gradient(135deg, #10b981, #059669)" icon={<TrendingUp sx={{ fontSize: 22 }} />} trend="up"
            onClick={() => navigate('/invoices')} />
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <StatCard title="Margin"
            value={col.monthlyCollected > 0 ? `${Math.round((col.monthlyProfit || 0) / col.monthlyCollected * 100)}%` : '—'}
            subtitle="Profit ratio"
            gradient="linear-gradient(135deg, #ec4899, #db2777)" icon={<Percent sx={{ fontSize: 22 }} />} />
        </Grid>
      </Grid>

      {/* Row 2: Invoice cards + billing breakdown */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard title="Provider Invoices" value={inv.providerCount || 0}
            subtitle={`Unpaid: ${fmt(inv.unpaidProviderAmount)}`}
            gradient="linear-gradient(135deg, #3b82f6, #1d4ed8)" icon={<Receipt sx={{ fontSize: 22 }} />}
            trend={inv.unpaidProviderAmount > 0 ? 'down' : undefined}
            onClick={() => navigate('/invoices')} />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard title="Contractor Invoices" value={inv.contractorCount || 0}
            subtitle={`Unpaid: ${fmt(inv.unpaidContractorAmount)}`}
            gradient="linear-gradient(135deg, #ef4444, #dc2626)" icon={<Receipt sx={{ fontSize: 22 }} />}
            trend={inv.unpaidContractorAmount > 0 ? 'down' : undefined}
            onClick={() => navigate('/invoices')} />
        </Grid>
        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%' }}>
            <CardContent sx={{ p: 3, '&:last-child': { pb: 3 } }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Avatar sx={{ width: 32, height: 32, background: 'linear-gradient(135deg, #3b82f6, #8b5cf6)', borderRadius: 2 }}>
                    <Assessment sx={{ fontSize: 18 }} />
                  </Avatar>
                  <Typography sx={{ fontWeight: 700, fontSize: '0.85rem' }}>Billing Breakdown</Typography>
                </Box>
              </Box>
              <ProgressRow label="Collections" value={col.monthlyCollected || 0} total={Math.max(col.monthlyCollected || 1, 10000)} color="#3b82f6" />
              <ProgressRow label="Amount Billed" value={col.monthlyBilled || 0} total={col.monthlyCollected || 1} color="#f59e0b" />
              <ProgressRow label="Profit" value={col.monthlyProfit || 0} total={col.monthlyCollected || 1} color="#10b981" />
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Row 3: Financial Summary + Credentials */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent sx={{ p: 3, '&:last-child': { pb: 3 } }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2.5 }}>
                <Avatar sx={{ width: 32, height: 32, background: 'linear-gradient(135deg, #10b981, #059669)', borderRadius: 2 }}>
                  <TrendingUp sx={{ fontSize: 18 }} />
                </Avatar>
                <Typography sx={{ fontWeight: 700, fontSize: '0.85rem' }}>Financial Summary</Typography>
              </Box>
              <TableContainer>
                <Table size="small">
                  <TableBody>
                    {[
                      { label: 'Total Collected', value: fmtFull(col.monthlyCollected), color: '#3b82f6', dot: '#3b82f6' },
                      { label: 'Total Billed', value: fmtFull(col.monthlyBilled), color: '#f59e0b', dot: '#f59e0b' },
                      { label: 'Total Commission', value: fmtFull(col.monthlyCommission || 0), color: '#8b5cf6', dot: '#8b5cf6' },
                      { label: 'Gross Profit', value: fmtFull(col.monthlyProfit), color: '#10b981', dot: '#10b981' },
                      { label: 'Outstanding (Providers)', value: fmtFull(inv.unpaidProviderAmount), color: '#ef4444', dot: '#ef4444' },
                      { label: 'Outstanding (Contractors)', value: fmtFull(inv.unpaidContractorAmount), color: '#ef4444', dot: '#ef4444' },
                    ].map((r, i) => (
                      <TableRow key={i} sx={{ '&:last-child td': { border: 0 } }}>
                        <TableCell sx={{ py: 1.3, pl: 0, border: 0 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: r.dot, flexShrink: 0 }} />
                            <Typography sx={{ fontSize: '0.84rem', fontWeight: 500 }}>{r.label}</Typography>
                          </Box>
                        </TableCell>
                        <TableCell align="right" sx={{ py: 1.3, pr: 0, border: 0 }}>
                          <Typography sx={{ fontSize: '0.84rem', fontWeight: 700, color: r.color }}>{r.value}</Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card>
            <CardContent sx={{ p: 3, '&:last-child': { pb: 3 } }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Avatar sx={{ width: 32, height: 32, background: 'linear-gradient(135deg, #f59e0b, #d97706)', borderRadius: 2 }}>
                    <Warning sx={{ fontSize: 18 }} />
                  </Avatar>
                  <Typography sx={{ fontWeight: 700, fontSize: '0.85rem' }}>Expiring Credentials</Typography>
                </Box>
                <Chip size="small" label={creds.length} sx={{
                  fontWeight: 700, height: 24,
                  bgcolor: creds.length > 0 ? alpha('#f59e0b', 0.12) : alpha('#10b981', 0.12),
                  color: creds.length > 0 ? '#d97706' : '#059669',
                }} />
              </Box>

              {creds.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 5 }}>
                  <CheckCircle sx={{ fontSize: 48, color: '#10b981', mb: 1 }} />
                  <Typography sx={{ fontWeight: 600, color: 'text.secondary' }}>All credentials up to date</Typography>
                </Box>
              ) : (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ pl: 0 }}>Provider</TableCell>
                        <TableCell>Type</TableCell>
                        <TableCell>Expires</TableCell>
                        <TableCell align="right" sx={{ pr: 0 }}>Status</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {creds.slice(0, 6).map((cr, i) => {
                        const days = cr.daysUntilExpiry || 999;
                        const chipColor = days <= 30 ? '#ef4444' : days <= 60 ? '#f59e0b' : '#3b82f6';
                        return (
                          <TableRow key={i} sx={{ '&:last-child td': { border: 0 } }}>
                            <TableCell sx={{ py: 1.2, pl: 0 }}>
                              <Typography noWrap sx={{ fontWeight: 600, fontSize: '0.82rem', maxWidth: 120 }}>
                                {fullName(cr.provider) || 'N/A'}
                              </Typography>
                            </TableCell>
                            <TableCell sx={{ py: 1.2 }}>
                              <Typography sx={{ fontSize: '0.78rem', color: 'text.secondary' }}>
                                {cr.applicationName || cr.licenseType || 'License'}
                              </Typography>
                            </TableCell>
                            <TableCell sx={{ py: 1.2 }}>
                              <Typography sx={{ fontSize: '0.78rem' }}>
                                {cr.expiryDate ? new Date(cr.expiryDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '—'}
                              </Typography>
                            </TableCell>
                            <TableCell align="right" sx={{ py: 1.2, pr: 0 }}>
                              <Chip size="small" label={`${days}d`} sx={{
                                fontWeight: 700, height: 24, bgcolor: alpha(chipColor, 0.1), color: chipColor
                              }} />
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Row 4: Reminders / To-Dos */}
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Card>
            <CardContent sx={{ p: 3, '&:last-child': { pb: 3 } }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Avatar sx={{ width: 32, height: 32, background: 'linear-gradient(135deg, #ef4444, #dc2626)', borderRadius: 2 }}>
                    <NotificationsActive sx={{ fontSize: 18 }} />
                  </Avatar>
                  <Typography sx={{ fontWeight: 700, fontSize: '0.85rem' }}>Reminders & To-Dos</Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  {(reminders.counts?.overdue || 0) > 0 && (
                    <Chip size="small" label={`${reminders.counts.overdue} overdue`} sx={{ fontWeight: 700, height: 24, bgcolor: alpha('#ef4444', 0.12), color: '#ef4444' }} />
                  )}
                  {(reminders.counts?.today || 0) > 0 && (
                    <Chip size="small" label={`${reminders.counts.today} today`} sx={{ fontWeight: 700, height: 24, bgcolor: alpha('#f59e0b', 0.12), color: '#d97706' }} />
                  )}
                  {(reminders.counts?.upcoming || 0) > 0 && (
                    <Chip size="small" label={`${reminders.counts.upcoming} upcoming`} sx={{ fontWeight: 700, height: 24, bgcolor: alpha('#3b82f6', 0.12), color: '#3b82f6' }} />
                  )}
                </Box>
              </Box>

              {(reminders.overdue?.length || 0) + (reminders.today?.length || 0) + (reminders.upcoming?.length || 0) === 0 ? (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <CheckCircle sx={{ fontSize: 48, color: '#10b981', mb: 1 }} />
                  <Typography sx={{ fontWeight: 600, color: 'text.secondary' }}>No pending reminders</Typography>
                  <Typography sx={{ fontSize: '0.82rem', color: '#94a3b8', mt: 0.5 }}>Set reminders from the Invoices page</Typography>
                </Box>
              ) : (
                <Grid container spacing={2}>
                  {/* Overdue */}
                  {reminders.overdue?.length > 0 && (
                    <Grid item xs={12} md={4}>
                      <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, textTransform: 'uppercase', color: '#ef4444', mb: 1, letterSpacing: '0.08em' }}>
                        Overdue ({reminders.overdue.length})
                      </Typography>
                      {reminders.overdue.slice(0, 5).map((r, i) => (
                        <Box key={r.id || i} sx={{ p: 1.5, mb: 1, borderRadius: 2, bgcolor: alpha('#ef4444', 0.04), borderLeft: '3px solid #ef4444' }}>
                          <Typography sx={{ fontWeight: 600, fontSize: '0.82rem' }}>{r.title}</Typography>
                          <Typography sx={{ fontSize: '0.72rem', color: '#94a3b8' }}>
                            {r.reminderDate ? new Date(r.reminderDate).toLocaleDateString() : ''} {r.relatedInvoiceNumber ? `• ${r.relatedInvoiceNumber}` : ''}
                          </Typography>
                        </Box>
                      ))}
                    </Grid>
                  )}
                  {/* Today */}
                  {reminders.today?.length > 0 && (
                    <Grid item xs={12} md={4}>
                      <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, textTransform: 'uppercase', color: '#f59e0b', mb: 1, letterSpacing: '0.08em' }}>
                        Today ({reminders.today.length})
                      </Typography>
                      {reminders.today.slice(0, 5).map((r, i) => (
                        <Box key={r.id || i} sx={{ p: 1.5, mb: 1, borderRadius: 2, bgcolor: alpha('#f59e0b', 0.04), borderLeft: '3px solid #f59e0b' }}>
                          <Typography sx={{ fontWeight: 600, fontSize: '0.82rem' }}>{r.title}</Typography>
                          <Typography sx={{ fontSize: '0.72rem', color: '#94a3b8' }}>
                            {r.reminderTime || ''} {r.relatedInvoiceNumber ? `• ${r.relatedInvoiceNumber}` : ''}
                          </Typography>
                        </Box>
                      ))}
                    </Grid>
                  )}
                  {/* Upcoming */}
                  {reminders.upcoming?.length > 0 && (
                    <Grid item xs={12} md={4}>
                      <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, textTransform: 'uppercase', color: '#3b82f6', mb: 1, letterSpacing: '0.08em' }}>
                        Upcoming ({reminders.upcoming.length})
                      </Typography>
                      {reminders.upcoming.slice(0, 5).map((r, i) => (
                        <Box key={r.id || i} sx={{ p: 1.5, mb: 1, borderRadius: 2, bgcolor: alpha('#3b82f6', 0.04), borderLeft: '3px solid #3b82f6' }}>
                          <Typography sx={{ fontWeight: 600, fontSize: '0.82rem' }}>{r.title}</Typography>
                          <Typography sx={{ fontSize: '0.72rem', color: '#94a3b8' }}>
                            {r.reminderDate ? new Date(r.reminderDate).toLocaleDateString() : ''} {r.relatedInvoiceNumber ? `• ${r.relatedInvoiceNumber}` : ''}
                          </Typography>
                        </Box>
                      ))}
                    </Grid>
                  )}
                </Grid>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default Dashboard;
